/**
 * Serviço de projetos.
 *
 * Responsabilidade:
 * - CRUD de projetos no Firestore (coleção `projects`)
 * - Listagem por usuário (dashboard, página de projetos)
 * - Atualização de visibilidade (private | shared | public)
 * - Projetos públicos do portfólio (/u/:slug)
 *
 * Modelo de referência: Project em docs/architecture.md
 * (id, userId, title, description, clientName, visibility, coverImage, createdAt, updatedAt)
 *
 * @see docs/firebase-foundation.md
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import { getImagesByProjectId } from "@/services/images/imageService";
import { deleteAllHotspotsForImage } from "@/services/hotspots/hotspotService";
import {
  assertCanCreateProject,
  assertProjectEmbedEnabled,
  assertPublicVisibilityEnabled,
} from "@/services/plans/planService";
import {
  collectImageStoragePaths,
  deleteImageFilesTolerant,
} from "@/services/storage/storageService";
import { getActiveWorkspaceIdForUser } from "@/services/workspaces/workspaceService";
import {
  buildPaginationCursor,
  isPaginationCursor,
  toAppDate,
} from "@/services/firebase/dates";
import { getQuotaSizeBytes } from "@/utils/storageQuota";
import {
  normalizeEmbedSettings,
  resolveInitialImageId,
} from "@/services/projects/embedSettings";
import { sortByRecency } from "@/utils/recencySort";
import { visibilityToLabel } from "@/utils/visibility";
import { canAccessSharedProject } from "@/utils/publicAccess";
import {
  canUserAccessProjectInternally,
  logInternalProjectAccessDenied,
} from "@/utils/projectAccess";

/**
 * @typedef {Object} Project
 * @property {string} id
 * @property {string} userId
 * @property {string} title
 * @property {string} description
 * @property {string} clientName
 * @property {'private' | 'shared' | 'public'} visibility
 * @property {string} coverImage
 * @property {number} imageCount
 * @property {import("@/services/projects/embedSettings").ProjectEmbedSettings} embedSettings
 * @property {Date | null} [createdAt]
 * @property {Date | null} [updatedAt]
 * @property {string} [workspaceId]
 */

/**
 * @param {string} projectId
 * @param {import("firebase/firestore").DocumentData} data
 * @returns {Project}
 */
function mapProjectDoc(projectId, data) {
  return {
    id: projectId,
    userId: data.userId ?? "",
    title: data.title ?? "",
    description: data.description ?? "",
    clientName: data.clientName ?? "",
    visibility: data.visibility ?? "private",
    coverImage: data.coverImage ?? "",
    imageCount: data.imageCount ?? 0,
    embedSettings: normalizeEmbedSettings(data.embedSettings),
    createdAt: toAppDate(data.createdAt),
    updatedAt: toAppDate(data.updatedAt),
    workspaceId: data.workspaceId ?? "",
  };
}

/**
 * Lista todos os projetos de um usuário (dashboard, /projects).
 *
 * @param {string} userId
 * @returns {Promise<Project[]>}
 */
export async function getProjectsByUserId(userId) {
  const projectsQuery = query(
    collection(db, "projects"),
    where("userId", "==", userId),
  );
  const snapshot = await getDocs(projectsQuery);

  return sortByRecency(
    snapshot.docs.map((docSnap) => mapProjectDoc(docSnap.id, docSnap.data())),
  );
}

/**
 * @typedef {Object} ProjectsPageResult
 * @property {Project[]} items
 * @property {import("@/services/firebase/dates").PaginationCursor | null} cursor
 * @property {boolean} hasMore
 */

/**
 * Lista projetos paginados por updatedAt DESC (fallback createdAt na ordenação local).
 * Requer índice composto: projects — userId ASC, updatedAt DESC.
 *
 * @param {string} userId
 * @param {{ limitCount: number, cursor?: import("@/services/firebase/dates").PaginationCursor | null }} options
 * @returns {Promise<ProjectsPageResult>}
 */
export async function getProjectsPageByUserId(
  userId,
  { limitCount, cursor = null },
) {
  const constraints = [
    where("userId", "==", userId),
    orderBy("updatedAt", "desc"),
  ];

  if (isPaginationCursor(cursor)) {
    const cursorSnap = await getDoc(doc(db, "projects", cursor.id));
    if (cursorSnap.exists()) {
      constraints.push(startAfter(cursorSnap));
    } else if (cursor.sortValue != null) {
      constraints.push(startAfter(Timestamp.fromMillis(cursor.sortValue)));
    }
  }

  constraints.push(limit(limitCount + 1));

  const projectsQuery = query(collection(db, "projects"), ...constraints);

  try {
    const snapshot = await getDocs(projectsQuery);
    const docs = snapshot.docs;
    const hasMore = docs.length > limitCount;
    const pageDocs = hasMore ? docs.slice(0, limitCount) : docs;

    const items = sortByRecency(
      pageDocs.map((docSnap) => mapProjectDoc(docSnap.id, docSnap.data())),
    );

    const last = pageDocs.at(-1);

    return {
      items,
      cursor: last
        ? buildPaginationCursor(last.id, last.data().updatedAt)
        : null,
      hasMore,
    };
  } catch (error) {
    if (error?.code === "failed-precondition") {
      console.error(
        "[getProjectsPageByUserId] Índice Firestore necessário: collection(projects) where userId ==, orderBy updatedAt desc",
        error,
      );
    }

    throw error;
  }
}

/**
 * Carrega um projeto pelo ID (sem filtro de ownership/visibilidade).
 *
 * Preferir:
 * - {@link getOwnedOrAccessibleProject} na rota interna `/projects/:id`
 * - {@link getPublicSharedProject} (ou `canAccessSharedProject`) nas rotas públicas
 *
 * @param {string} projectId
 * @returns {Promise<Project | null>}
 */
export async function getProjectById(projectId) {
  const snapshot = await getDoc(doc(db, "projects", projectId));

  if (!snapshot.exists()) {
    return null;
  }

  return mapProjectDoc(snapshot.id, snapshot.data());
}

/**
 * Projeto acessível no contexto privado/autenticado.
 * Visibilidade pública NÃO concede acesso.
 *
 * @param {string} projectId
 * @param {string} userId
 * @param {{
 *   membership?: {
 *     userId?: string,
 *     workspaceId?: string,
 *     role?: string,
 *   } | null,
 *   activeWorkspaceId?: string | null,
 * }} [options]
 * @returns {Promise<Project | null>}
 */
export async function getOwnedOrAccessibleProject(
  projectId,
  userId,
  options = {},
) {
  if (!projectId || !userId) {
    return null;
  }

  const project = await getProjectById(projectId);

  if (!project) {
    return null;
  }

  const allowed = canUserAccessProjectInternally({
    userId,
    project,
    membership: options.membership ?? null,
    activeWorkspaceId: options.activeWorkspaceId ?? null,
  });

  if (!allowed) {
    logInternalProjectAccessDenied(projectId, "not_owner_or_member");
    return null;
  }

  return project;
}

/**
 * Projeto acessível via link público compartilhado (`shared` | `public`).
 * Não usar na rota interna `/projects/:id`.
 *
 * @param {string} projectId
 * @returns {Promise<Project | null>}
 */
export async function getPublicSharedProject(projectId) {
  if (!projectId) {
    return null;
  }

  const project = await getProjectById(projectId);

  if (!project || !canAccessSharedProject(project)) {
    return null;
  }

  return project;
}

/**
 * Cria um novo projeto.
 * coverImage inicia vazio; na Sprint 3.2 será preenchido pela primeira imagem.
 *
 * @param {string} userId
 * @param {{
 *   title: string,
 *   description?: string,
 *   clientName?: string,
 *   visibility?: Project['visibility'],
 *   coverImage?: string,
 * }} data
 * @returns {Promise<string>} ID do projeto criado
 */
export async function createProject(userId, data) {
  await assertCanCreateProject(userId);

  if (data.visibility === "public") {
    await assertPublicVisibilityEnabled(userId);
  }

  const workspaceId = await getActiveWorkspaceIdForUser(userId);

  const docRef = await addDoc(collection(db, "projects"), {
    userId,
    workspaceId,
    title: data.title.trim(),
    description: data.description?.trim() ?? "",
    clientName: data.clientName?.trim() ?? "",
    visibility: data.visibility ?? "private",
    coverImage: "",
    imageCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Atualiza campos de um projeto existente.
 *
 * @param {string} projectId
 * @param {Partial<Pick<Project, 'title' | 'description' | 'clientName' | 'visibility' | 'coverImage'>>} data
 */
export async function updateProject(projectId, data) {
  if (data.visibility === "public") {
    const project = await getProjectById(projectId);

    if (project?.userId) {
      await assertPublicVisibilityEnabled(project.userId);
    }
  }

  const updates = { updatedAt: serverTimestamp() };

  if (data.title !== undefined) {
    updates.title = data.title.trim();
  }
  if (data.description !== undefined) {
    updates.description = data.description.trim();
  }
  if (data.clientName !== undefined) {
    updates.clientName = data.clientName.trim();
  }
  if (data.visibility !== undefined) {
    updates.visibility = data.visibility;
  }
  if (data.coverImage !== undefined) {
    updates.coverImage = data.coverImage.trim();
  }

  await updateDoc(doc(db, "projects", projectId), updates);
}

/**
 * Atualiza embedSettings do projeto (owner + plano Professional+).
 * showBranding é forçado a true nesta sprint.
 *
 * @param {string} projectId
 * @param {string} userId
 * @param {Partial<{
 *   enabled: boolean,
 *   initialImageId: string | null,
 *   allowFullscreen: boolean,
 *   allowNavigation: boolean,
 * }>} patch
 * @returns {Promise<import("@/services/projects/embedSettings").ProjectEmbedSettings>}
 */
export async function updateProjectEmbedSettings(projectId, userId, patch) {
  await assertProjectEmbedEnabled(userId);

  const project = await getProjectById(projectId);

  if (!project) {
    throw new Error("Projeto não encontrado.");
  }

  if (project.userId !== userId) {
    throw new Error("Sem permissão para atualizar a incorporação deste projeto.");
  }

  const current = project.embedSettings;
  let nextInitialImageId =
    patch.initialImageId !== undefined
      ? patch.initialImageId
      : current.initialImageId;

  if (nextInitialImageId !== null && nextInitialImageId !== undefined) {
    const images = await getImagesByProjectId(projectId, userId);
    const resolved = resolveInitialImageId(nextInitialImageId, images);

    if (
      typeof nextInitialImageId === "string" &&
      nextInitialImageId &&
      resolved !== nextInitialImageId
    ) {
      throw new Error("A imagem inicial deve pertencer ao projeto.");
    }

    nextInitialImageId = resolved;
  } else {
    nextInitialImageId = null;
  }

  const nextSettings = {
    enabled: patch.enabled !== undefined ? patch.enabled === true : current.enabled,
    initialImageId: nextInitialImageId,
    allowFullscreen:
      patch.allowFullscreen !== undefined
        ? patch.allowFullscreen === true
        : current.allowFullscreen,
    allowNavigation:
      patch.allowNavigation !== undefined
        ? patch.allowNavigation === true
        : current.allowNavigation,
    showBranding: true,
    updatedAt: serverTimestamp(),
  };

  await updateDoc(doc(db, "projects", projectId), {
    embedSettings: nextSettings,
    updatedAt: serverTimestamp(),
  });

  return normalizeEmbedSettings({
    ...nextSettings,
    updatedAt: new Date(),
  });
}

/**
 * Ajusta o contador agregado de imagens do projeto (incremento atômico no Firestore).
 *
 * @param {string} projectId
 * @param {number} delta
 * @param {Partial<Pick<Project, 'coverImage'>>} [extraUpdates]
 */
export async function adjustProjectImageCount(projectId, delta, extraUpdates = {}) {
  const updates = {
    updatedAt: serverTimestamp(),
    imageCount: increment(delta),
  };

  if (extraUpdates.coverImage !== undefined) {
    updates.coverImage = extraUpdates.coverImage.trim();
  }

  await updateDoc(doc(db, "projects", projectId), updates);
}

/**
 * @typedef {Object} DeleteProjectCascadeResult
 * @property {number} deletedImageCount
 * @property {number} deletedStorageBytes — soma de quota comercial (originalSizeBytes)
 * @property {number} deletedHotspotCount
 * @property {string[]} imageIds
 */

/**
 * Exclui projeto e todo o conteúdo vinculado: imagens, hotspots e arquivos no Storage.
 *
 * Ordem: imagens (hotspots → storage → doc) → documento do projeto.
 * Falhas no Storage não interrompem o fluxo; falhas no Firestore propagam erro.
 *
 * @param {string} projectId
 * @param {string} userId
 * @returns {Promise<DeleteProjectCascadeResult>}
 */
export async function deleteProjectCascade(projectId, userId) {
  if (!projectId || !userId) {
    throw new Error("Dados incompletos para excluir o projeto.");
  }

  const project = await getProjectById(projectId);

  if (!project) {
    throw new Error("Projeto não encontrado.");
  }

  if (project.userId !== userId) {
    throw new Error("Sem permissão para excluir este projeto.");
  }

  const images = await getImagesByProjectId(projectId, userId);

  let deletedImageCount = 0;
  let deletedStorageBytes = 0;
  let deletedHotspotCount = 0;

  for (const image of images) {
    deletedHotspotCount += await deleteAllHotspotsForImage(image.id);

    const storagePaths = collectImageStoragePaths(image) || [];
    await deleteImageFilesTolerant(storagePaths);

    try {
      await deleteDoc(doc(db, "images", image.id));
      deletedImageCount += 1;
      deletedStorageBytes += getQuotaSizeBytes(image);
    } catch (error) {
      console.error(
        "[deleteProjectCascade] Falha ao excluir imagem do Firestore:",
        image.id,
        error,
      );
      throw new Error(
        "Não foi possível excluir as imagens do projeto. Tente novamente.",
      );
    }
  }

  try {
    await deleteDoc(doc(db, "projects", projectId));
  } catch (error) {
    console.error(
      "[deleteProjectCascade] Falha ao excluir projeto do Firestore:",
      projectId,
      error,
    );
    throw new Error("Não foi possível excluir o projeto. Tente novamente.");
  }

  return {
    deletedImageCount,
    deletedStorageBytes,
    deletedHotspotCount,
    imageIds: images.map((image) => image.id),
  };
}

/**
 * @deprecated Use {@link deleteProjectCascade} com `userId` para exclusão completa.
 * @param {string} projectId
 */
export async function deleteProject(projectId) {
  await deleteDoc(doc(db, "projects", projectId));
}

/**
 * Lista projetos públicos de um usuário (portfólio /u/:slug).
 *
 * @param {string} userId
 * @returns {Promise<Project[]>}
 */
export async function getPublicProjectsByUserId(userId) {
  const projectsQuery = query(
    collection(db, "projects"),
    where("userId", "==", userId),
    where("visibility", "==", "public"),
  );
  const snapshot = await getDocs(projectsQuery);

  return sortByRecency(
    snapshot.docs.map((docSnap) => mapProjectDoc(docSnap.id, docSnap.data())),
  );
}

/**
 * Sincroniza imageCount de todos os projetos do usuário a partir das imagens existentes.
 * Utilitário para backfill manual (projetos legados sem imageCount).
 * Não chamar na listagem paginada — usa 1 query de imagens + 1 de projetos.
 *
 * @param {string} userId
 * @returns {Promise<void>}
 */
export async function backfillProjectImageCounts(userId) {
  const imagesQuery = query(
    collection(db, "images"),
    where("userId", "==", userId),
  );
  const imagesSnapshot = await getDocs(imagesQuery);

  /** @type {Map<string, number>} */
  const countsByProjectId = new Map();

  for (const docSnap of imagesSnapshot.docs) {
    const rawProjectId = docSnap.data().projectId;

    if (rawProjectId === undefined || rawProjectId === null || rawProjectId === "") {
      continue;
    }

    countsByProjectId.set(
      rawProjectId,
      (countsByProjectId.get(rawProjectId) ?? 0) + 1,
    );
  }

  const projects = await getProjectsByUserId(userId);

  await Promise.all(
    projects
      .filter((project) => {
        const expectedCount = countsByProjectId.get(project.id) ?? 0;
        return project.imageCount !== expectedCount;
      })
      .map((project) =>
        updateDoc(doc(db, "projects", project.id), {
          imageCount: countsByProjectId.get(project.id) ?? 0,
        }),
      ),
  );
}

/**
 * Adapta um projeto Firestore para o formato do ProjectCard.
 *
 * @param {Project} project
 * @returns {{ id: string, name: string, cover: string, images: number, status: string }}
 */
export function mapProjectToCard(project) {
  return {
    id: project.id,
    name: project.title || "Sem título",
    cover: project.coverImage ?? "",
    images: project.imageCount ?? 0,
    status: visibilityToLabel(project.visibility),
  };
}
